import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';

const STORAGE_KEY = 'retain-iq-call-log-tabs';
const MAX_TABS = 8;

export type CallLogPayload = Record<string, unknown>;

export type BusinessRowSnapshot = {
  id: string;
  business_name?: string;
  name?: string;
  store_id?: string | null;
  mid?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  contact_person?: string | null;
  phone?: string | null;
  cell_phone?: string | null;
  email?: string | null;
  services?: string[];
};

export type CallLogDraft = {
  businessId?: string;
  selectedBusiness: BusinessRowSnapshot | null;
  businessSearch: string;
  agentUserId?: string;
  payload: CallLogPayload;
  createTicket: boolean;
  linkTicketInput: string;
  ticketSubject: string;
  ticketType?: string;
  ticketCategoryId?: string;
  ticketPriority: string;
  ticketComment: string;
};

export type CallLogTab = {
  id: string;
  label: string;
  draft: CallLogDraft;
};

export function createEmptyPayload(): CallLogPayload {
  return {
    call_date: dayjs().format('YYYY-MM-DD'),
    call_time: dayjs().format('HH:mm'),
    call_duration: '',
    call_type: '',
    contact_method: 'Phone Call',
    contact_made: 'Reached',
    merchant_id: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    business_services: [] as string[],
    account_tenure: '',
    risk_level: '',
    topics: [] as string[],
    topics_other: '',
    sentiment: '',
    pain_points: '',
    positive_notes: '',
    commitments: '',
    followup_flag: 'No',
    followup_date: '',
    followup_action: '',
    call_outcome: '',
    nps_score: 5,
    call_notes: '',
  };
}

export function createEmptyDraft(): CallLogDraft {
  return {
    businessId: undefined,
    selectedBusiness: null,
    businessSearch: '',
    agentUserId: undefined,
    payload: createEmptyPayload(),
    createTicket: false,
    linkTicketInput: '',
    ticketSubject: '',
    ticketType: undefined,
    ticketCategoryId: undefined,
    ticketPriority: 'medium',
    ticketComment: '',
  };
}

function newTabId(): string {
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function loadFromStorage(): { tabs: CallLogTab[]; activeTabId: string } {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const id = newTabId();
      return { tabs: [{ id, label: 'New call', draft: createEmptyDraft() }], activeTabId: id };
    }
    const parsed = JSON.parse(raw) as { tabs?: CallLogTab[]; activeTabId?: string };
    if (!parsed.tabs?.length) {
      const id = newTabId();
      return { tabs: [{ id, label: 'New call', draft: createEmptyDraft() }], activeTabId: id };
    }
    return {
      tabs: parsed.tabs.slice(-MAX_TABS),
      activeTabId: parsed.activeTabId && parsed.tabs.some((t) => t.id === parsed.activeTabId)
        ? parsed.activeTabId
        : parsed.tabs[0].id,
    };
  } catch {
    const id = newTabId();
    return { tabs: [{ id, label: 'New call', draft: createEmptyDraft() }], activeTabId: id };
  }
}

type ContextValue = {
  tabs: CallLogTab[];
  activeTabId: string;
  activeDraft: CallLogDraft;
  addTab: () => void;
  removeTab: (id: string) => void;
  switchTab: (id: string) => void;
  updateActiveDraft: (patch: Partial<CallLogDraft> | ((prev: CallLogDraft) => CallLogDraft)) => void;
  setActiveTabLabel: (label: string) => void;
  resetActiveDraft: () => void;
};

const RetainIQCallLogTabsContext = createContext<ContextValue | null>(null);

export const RetainIQCallLogTabsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [initial] = useState(loadFromStorage);
  const [tabs, setTabs] = useState<CallLogTab[]>(initial.tabs);
  const [activeTabId, setActiveTabId] = useState(initial.activeTabId);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ tabs, activeTabId }));
    } catch {
      /* ignore */
    }
  }, [tabs, activeTabId]);

  const activeTab = tabs.find((t) => t.id === activeTabId) || tabs[0];
  const activeDraft = activeTab?.draft ?? createEmptyDraft();

  const updateActiveDraft = useCallback(
    (patch: Partial<CallLogDraft> | ((prev: CallLogDraft) => CallLogDraft)) => {
      setTabs((prev) =>
        prev.map((t) => {
          if (t.id !== activeTabId) return t;
          const nextDraft = typeof patch === 'function' ? patch(t.draft) : { ...t.draft, ...patch };
          return { ...t, draft: nextDraft };
        })
      );
    },
    [activeTabId]
  );

  const setActiveTabLabel = useCallback(
    (label: string) => {
      setTabs((prev) => prev.map((t) => (t.id === activeTabId ? { ...t, label } : t)));
    },
    [activeTabId]
  );

  const addTab = useCallback(() => {
    const id = newTabId();
    setTabs((prev) => [...prev, { id, label: 'New call', draft: createEmptyDraft() }].slice(-MAX_TABS));
    setActiveTabId(id);
  }, []);

  const removeTab = useCallback(
    (id: string) => {
      setTabs((prev) => {
        if (prev.length <= 1) {
          const only = prev[0];
          if (only.id === id) {
            const fresh = createEmptyDraft();
            return [{ ...only, label: 'New call', draft: fresh }];
          }
        }
        const idx = prev.findIndex((t) => t.id === id);
        const next = prev.filter((t) => t.id !== id);
        if (id === activeTabId && next.length > 0) {
          const fallback = next[Math.max(0, idx - 1)] ?? next[0];
          setActiveTabId(fallback.id);
        }
        if (next.length === 0) {
          const newId = newTabId();
          setActiveTabId(newId);
          return [{ id: newId, label: 'New call', draft: createEmptyDraft() }];
        }
        return next;
      });
    },
    [activeTabId]
  );

  const switchTab = useCallback((id: string) => {
    setActiveTabId(id);
  }, []);

  const resetActiveDraft = useCallback(() => {
    setTabs((prev) =>
      prev.map((t) => (t.id === activeTabId ? { ...t, label: 'New call', draft: createEmptyDraft() } : t))
    );
  }, [activeTabId]);

  return (
    <RetainIQCallLogTabsContext.Provider
      value={{
        tabs,
        activeTabId,
        activeDraft,
        addTab,
        removeTab,
        switchTab,
        updateActiveDraft,
        setActiveTabLabel,
        resetActiveDraft,
      }}
    >
      {children}
    </RetainIQCallLogTabsContext.Provider>
  );
};

export function useRetainIQCallLogTabs() {
  const ctx = useContext(RetainIQCallLogTabsContext);
  if (!ctx) throw new Error('useRetainIQCallLogTabs must be used within RetainIQCallLogTabsProvider');
  return ctx;
}
